import { IkeToken } from '@ikegps/core-api';
import { action, makeObservable, observable } from 'mobx';
import { T } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { ImageButton } from '../button/image.button';
import { MaterialSymbol } from '../icon/icons';

/**
 * One key across every screen, so collapsing on one arrives collapsed on the next. Held here rather than
 * passed in: the screens must not be able to drift onto separate keys.
 */
const COLLAPSE_STORAGE_KEY = 'panel.collapsed/list';

export interface PanelCollapseOptions {
	/** Base name only, e.g. `--ike-panel-map-list`; the `-collapsed` and `-vis` siblings are derived. */
	cssVar: string;
	onToggle?: () => void;
}

/**
 * Layered rather than sharing one property: a collapse must not clobber the width the user dragged the
 * panel to (UFO-1108), which lives in the very property `expanded` reads.
 */
export function panelWidthValue(cssVar: string, expanded: string): string {
	return `var(${cssVar}-collapsed, ${expanded})`;
}

export function panelVisibilityValue(cssVar: string): string {
	return `var(${cssVar}-vis, visible)`;
}

/**
 * Collapse is applied through custom properties on the document root rather than by dropping the panel
 * from the vnode tree. Two reasons: the panels are flex items the pages size by direct-child selector, so
 * a wrapper element would break the UFO-1108 sizing; and unmounting would tear down the live Google Map
 * inside the photo measure list, whose fragment carries the shared-map overlay teardown.
 *
 * Hidden with `visibility` and a zero width rather than `overflow: hidden` (which would clip the menus and
 * popups that legitimately escape the panel) or `display: none` (which resets the list's scroll position).
 */
export class PanelCollapseState {
	@observable private collapsed: boolean = false;
	private restored: boolean = false;

	constructor(private readonly options: PanelCollapseOptions) {
		makeObservable(this);
	}

	get isEnabled(): boolean {
		return Services.Token.has(IkeToken.FEATURE_COMPONENT_USABILITY);
	}

	get isCollapsed(): boolean {
		return this.isEnabled && this.collapsed;
	}

	/**
	 * Call from the page's own init, not a Mithril lifecycle hook: this writes an observable, so restoring
	 * after the first paint would render the panel expanded and then snap it shut. One-shot per instance,
	 * and pages build one per visit, so the stored state reapplies on every visit.
	 *
	 * Applies the resolved state either way rather than only when collapsed. The properties sit on the
	 * document root and outlive the page, so a panel entering expanded has to actively clear whatever the
	 * previous page — or the previous department, which may have had the token — left there. Skipping that
	 * leaves a zero-width invisible panel with no rail to bring it back.
	 */
	ensureRestored() {
		if (this.restored) {
			return;
		}
		this.restored = true;

		// Token first: a department without it gets its panel back rather than a rail it can't expand.
		const collapsed = this.isEnabled && Services.LocalStorage.getItem<boolean>(COLLAPSE_STORAGE_KEY) === true;
		this.setCollapsed(collapsed);
	}

	@action toggle() {
		if (!this.isEnabled) {
			return;
		}

		this.setCollapsed(!this.collapsed);
		Services.LocalStorage.setItem(COLLAPSE_STORAGE_KEY, this.collapsed);

		if (this.options.onToggle) {
			this.options.onToggle();
		}
	}

	/**
	 * Through ImageButton so it can't drift from the buttons beside it, and with a native `title` rather
	 * than a styled bubble, which the map mode toolbar's `overflow: hidden` would clip.
	 */
	viewToggle(): MithrilVNode {
		if (!this.isEnabled) {
			return null;
		}

		const collapsed = this.collapsed;

		return ImageButton.viewSymbol(collapsed ? MaterialSymbol.LEFT_PANEL_OPEN : MaterialSymbol.LEFT_PANEL_CLOSE, {
			type: 'button',
			title: collapsed ? T.EXPAND_SIDEBAR : T.COLLAPSE_SIDEBAR,
			// Selecting on the title would couple the E2E to translated copy; both the toolbar and the rail
			// render one of these, so the spec scopes by container rather than by id alone.
			testId: 'panel-collapse-toggle',
			onclick: () => this.toggle(),
		});
	}

	/**
	 * Re-applies the document properties without touching the remembered state, for when the token changes
	 * under a live page: switching department keeps the same page instance (route.service only swaps pages
	 * when the route name changes), so neither the constructor nor `ensureRestored` runs again. Leaving
	 * `collapsed` alone is what lets the collapse come back when the token does.
	 */
	syncToToken() {
		this.applyProperties(this.isCollapsed);
	}

	@action private setCollapsed(value: boolean) {
		this.collapsed = value;
		this.applyProperties(value);
	}

	private applyProperties(collapsed: boolean) {
		const style = Services.Window.document.documentElement.style;
		if (collapsed) {
			style.setProperty(`${this.options.cssVar}-collapsed`, '0px');
			style.setProperty(`${this.options.cssVar}-vis`, 'hidden');
		} else {
			style.removeProperty(`${this.options.cssVar}-collapsed`);
			style.removeProperty(`${this.options.cssVar}-vis`);
		}
	}
}
